import defaults from '../styles/default-classes.json'

/** CSS classes of the built-in components, by component and element. Defaults: src/styles/default-classes.json */
export type Classes = typeof defaults

export type ClassGroup = keyof Classes

/** An override: per component only the elements to change, everything else keeps its default. */
export type ClassOverrides = { [Group in ClassGroup]?: Partial<Classes[Group]> }

export const defaultClasses: Classes = defaults

/**
 * Merges an override into the defaults, shallow per component: a given element replaces the default classes of that
 * element, unknown components and elements are ignored. A string is parsed as JSON (the `classes` attribute of the
 * web component); invalid JSON keeps the defaults.
 */
export function mergeClasses(overrides?: ClassOverrides | string | null): Classes {
    const parsed = typeof overrides === 'string' ? parseOverrides(overrides) : overrides
    const merged = {} as Record<string, Record<string, string>>

    for (const group of Object.keys(defaults) as ClassGroup[]) {
        const override = parsed?.[group]
        merged[group] = { ...defaults[group] }

        if (!isRecord(override)) {
            continue
        }

        for (const [element, value] of Object.entries(override)) {
            if (element in defaults[group] && typeof value === 'string') {
                merged[group][element] = value
            }
        }
    }

    return merged as Classes
}

function parseOverrides(json: string): ClassOverrides | null {
    if (json.trim() === '') {
        return null
    }

    try {
        const parsed: unknown = JSON.parse(json)

        return isRecord(parsed) ? (parsed as ClassOverrides) : null
    } catch {
        console.warn('[agent-search] the classes attribute is not valid JSON, the default classes are used')

        return null
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}
